import { financeCirculars } from '../data/circulars';
import { CircularLine, CircularTerm, FinanceCircular, FinanceOffer, Vehicle } from '../types/catalog';
import { isExpired } from './format';

export interface CircularQuote {
  circular: FinanceCircular;
  line: CircularLine;
  row: CircularTerm;
  price: number;
  financed: number;
  /** Diferencia entre el precio y el monto financiado. */
  down: number;
  /** Gestión crediticia + gastos de otorgamiento (% del capital financiado). */
  managementFee: number;
  installment: number;
  /** Lo que se abona al inicio: anticipo + gastos. */
  upfront: number;
  totalInstallments: number;
  totalCost: number;
}

/** Circular vigente para la marca de la unidad, si existe. */
export function getCircularFor(vehicle: Vehicle): FinanceCircular | null {
  const c = financeCirculars.find((x) => x.brandId === vehicle.brandId);
  return c && !isExpired(c.validUntil) ? c : null;
}

const norm = (s: string) => s.trim().toLowerCase();

export function lineAppliesTo(line: CircularLine, vehicle: Vehicle): boolean {
  if (line.models === 'all') return true;
  // Se compara contra modelo + versión para líneas como "Compass Sport".
  const label = norm(`${vehicle.model} ${vehicle.version}`);
  return line.models.some((m) => norm(m).split(/\s+/).every((word) => label.includes(word)));
}

export function eligibleLines(circular: FinanceCircular, vehicle: Vehicle): CircularLine[] {
  return circular.lines.filter((l) => lineAppliesTo(l, vehicle));
}

export function maxFinanceable(line: CircularLine, price: number): number {
  return Math.min(line.maxAmount, price);
}

export function quoteCircular(circular: FinanceCircular, line: CircularLine, row: CircularTerm, price: number, financed?: number): CircularQuote {
  const cap = maxFinanceable(line, price);
  const amount = Math.max(0, Math.min(cap, financed ?? cap));
  const installment = (amount / 1000) * row.installmentPer1000;
  const managementFee = Math.round((amount * (circular.managementFeePct ?? 0)) / 100);
  const down = price - amount;
  const totalInstallments = installment * row.term;
  return {
    circular,
    line,
    row,
    price,
    financed: amount,
    down,
    managementFee,
    installment,
    upfront: down + managementFee,
    totalInstallments,
    totalCost: down + managementFee + totalInstallments,
  };
}

/**
 * Cuota de referencia para tarjetas y comparador: línea de tasa fija para público general
 * con el mayor monto a financiar y el plazo más largo.
 */
export function bestCircularQuote(circular: FinanceCircular, vehicle: Vehicle): CircularQuote | null {
  const lines = eligibleLines(circular, vehicle).filter((l) => l.audience === 'general');
  const pool = lines.filter((l) => l.rateType === 'fija').length ? lines.filter((l) => l.rateType === 'fija') : lines;
  if (!pool.length) return null;
  const line = [...pool].sort((a, b) => b.maxAmount - a.maxAmount)[0];
  const row = [...line.terms].sort((a, b) => b.term - a.term)[0];
  return quoteCircular(circular, line, row, vehicle.price);
}

/** Traduce una cotización de circular al formato de condición que usa el resto del sitio. */
export function circularToOffer(q: CircularQuote): FinanceOffer {
  return {
    tna: q.row.tna,
    cft: null,
    amortization: 'frances',
    terms: q.line.terms.map((t) => t.term),
    minDownPct: 0,
    maxFinanceAmount: q.line.maxAmount,
    monthlyInsurance: null,
    adminFees: q.managementFee,
    taxNote: 'Cuota con IVA incluido. No incluye seguro automotor.',
    lender: q.circular.lender,
    promo: `${q.line.name} · ${q.line.detail}${q.line.rateType === 'uva' ? ' (cuotas en UVA)' : ''}`,
    validUntil: q.circular.validUntil,
    conditions: `${q.circular.reference}. Sujeto a aprobación crediticia. ${managementFeeText(q.circular)}`,
    verified: true,
    updatedAt: `${q.circular.validFrom}T00:00:00.000Z`,
  };
}

/** Texto de gastos de otorgamiento según lo que informe la circular. */
export function managementFeeText(c: FinanceCircular): string {
  return c.managementFeePct === null
    ? 'Gastos de otorgamiento: a confirmar por la entidad al aprobar el crédito.'
    : `Gestión crediticia y gastos de otorgamiento: ${c.managementFeePct}% del capital financiado (promedio estimado).`;
}

export const rateTypeLabel = (l: CircularLine) => (l.rateType === 'uva' ? 'Cuotas en UVA' : 'Tasa fija');
